import { useCallback, useEffect, useRef, useState } from 'react';
import { buildUnitWithoutEquippedWeaponsPresentation } from './drone79WeaponRevealPresentation';
import {
  buildMission3IntroOrbitalDropEntry,
  shouldScheduleMission3OrbitalDropIntro,
} from './drone79OrbitalDropMission3';

/**
 * While Drone 79 weapon-box animation plays (tutorial mission 3 intro), hide equipped
 * weapons on the target unit and show pre-weapon stats (e.g. Sankrat 1/3).
 */
export function useDroneOrbitalDropWeaponReveal(gameState, userId) {
  const [pendingInstanceIds, setPendingInstanceIds] = useState(() => new Set());
  const pendingRef = useRef(new Set());
  const gameIdRef = useRef(null);

  const syncPending = useCallback((nextSet) => {
    pendingRef.current = nextSet;
    setPendingInstanceIds(nextSet);
  }, []);

  const markPending = useCallback(
    (instanceId) => {
      if (instanceId == null || instanceId === '') {
        throw new Error('useDroneOrbitalDropWeaponReveal.markPending: instanceId is required');
      }
      const key = String(instanceId);
      if (pendingRef.current.has(key)) return;
      const next = new Set(pendingRef.current);
      next.add(key);
      syncPending(next);
    },
    [syncPending]
  );

  const clearPending = useCallback(
    (instanceId) => {
      if (instanceId == null || instanceId === '') return;
      const key = String(instanceId);
      if (!pendingRef.current.has(key)) return;
      const next = new Set(pendingRef.current);
      next.delete(key);
      syncPending(next);
    },
    [syncPending]
  );

  const isPending = useCallback((instanceId) => {
    if (instanceId == null || instanceId === '') return false;
    return pendingRef.current.has(String(instanceId));
  }, []);

  const presentWithoutWeapons = useCallback((unit) => {
    return buildUnitWithoutEquippedWeaponsPresentation(unit);
  }, []);

  useEffect(() => {
    const gid = gameState?.gameId ?? null;
    if (gid != null && gameIdRef.current !== gid) {
      gameIdRef.current = gid;
      syncPending(new Set());
    }
  }, [gameState?.gameId, syncPending]);

  useEffect(() => {
    if (!shouldScheduleMission3OrbitalDropIntro(gameState)) return;
    if (userId == null) return;
    const introEntry = buildMission3IntroOrbitalDropEntry(gameState, userId);
    if (!introEntry?.targetUnitInstanceId) return;
    markPending(introEntry.targetUnitInstanceId);
  }, [gameState, userId, markPending]);

  return {
    pendingInstanceIds,
    markPending,
    clearPending,
    isPending,
    presentWithoutWeapons,
  };
}

export default useDroneOrbitalDropWeaponReveal;
